/* Atlas V3 Desktop — Insights (The Weekly).
   Renders straight from useInsights().buckets (inflection / capital_flow /
   earnings_tells / quiet_signals). The lead story runs large; the rest group
   by bucket. Stories open a full-article READER overlay (useInsightDetail →
   body_md + extracted stats + source), ticker chips open the dock. */
import { useEffect, useMemo, useState } from 'react'
import { StockLogo } from '@/v2/components/StockLogo'
import { useTicker, Spark, intent } from '@/v2/v3/lib'
import { useInsights } from '@/v2/hooks/useInsights'
import { useInsightDetail } from '@/v2/hooks/useInsightDetail'
import { useLivePrices } from '@/v2/hooks/useLivePrices'
import { useTickerBars } from '@/v2/hooks/useChartBars'
import { Crossfade, SkelText, SkelEditorial } from '../qol'

const Serif = { fontFamily: 'var(--studio-font-display)' }
const Mono = { fontFamily: 'var(--studio-font-mono)' }
function pct(v) { return v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(2)}%` }
const BUCKETS = [
  ['inflection', 'Inflection'],
  ['capital_flow', 'Capital Flow'],
  ['earnings_tells', 'Earnings Tells'],
  ['quiet_signals', 'Quiet Signals'],
]

function tickersOf(story) {
  return (story.tickers || []).map((t) => (typeof t === 'string' ? { ticker: t } : t)).filter((t) => t.ticker)
}

export function Insights() {
  const openTicker = useTicker()
  const { buckets, loading } = useInsights()
  const [readerId, setReaderId] = useState(null)

  const lead = useMemo(() => {
    const all = BUCKETS.flatMap(([k]) => buckets[k] || [])
    return all.find((s) => s.is_hero) || (buckets.inflection || [])[0] || all[0] || null
  }, [buckets])

  const hasAny = BUCKETS.some(([k]) => (buckets[k] || []).length > 0)

  // Live rail for the lead story's tickers — fills the desktop right column
  // with real quote data (reuses the universe live-price + spark hooks).
  const leadTickers = useMemo(() => (lead ? tickersOf(lead).map((t) => t.ticker) : []), [lead])
  const { prices: leadPrices } = useLivePrices(leadTickers, 60_000)
  // 1D sparks to match the 1D % this rail shows (was the fixed ~10-day
  // /api/sparks shape); the lead story's ticker list is a small handful.
  const { barsByTicker } = useTickerBars(leadTickers, '1D')

  const TickerChips = ({ story }) => {
    const ts = tickersOf(story)
    if (ts.length === 0) return null
    return (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
        {ts.map((t) => (
          <button key={t.ticker} onClick={(e) => { e.stopPropagation(); openTicker(t.ticker) }} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 999, border: '1px solid hsl(var(--studio-border))', background: 'transparent', cursor: 'pointer' }}>
            <StockLogo ticker={t.ticker} size={15} />
            <span style={{ ...Mono, fontSize: 11, color: 'hsl(var(--studio-text))' }}>{t.ticker}{t.is_lead ? ' ·' : ''}</span>
          </button>
        ))}
      </div>
    )
  }

  return (
    <>
      <div className="v3web-phead">
        <div className="k">The Weekly</div>
        <div className="h" style={{ fontStyle: 'italic' }}>Insights</div>
      </div>

      {loading && !hasAny && (
        <Crossfade loading skeleton={
          <div>
            <div style={{ borderBottom: '1px solid hsl(var(--studio-border))', paddingBottom: 26, marginBottom: 30 }}>
              <SkelEditorial headline={38} />
            </div>
            <SkelText w={150} h={9} style={{ marginBottom: 16 }} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 26 }}>
              {[0, 1, 2, 3].map((i) => (
                <div key={i} style={{ borderTop: '1px solid hsl(var(--studio-border) / 0.5)', paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 9 }}>
                  <SkelText w="86%" h={19} />
                  <SkelText w="60%" h={19} />
                  <SkelText w="92%" h={11} />
                </div>
              ))}
            </div>
          </div>
        } />
      )}
      {!loading && !hasAny && <div className="v3web-empty"><div className="pe-k">No briefing yet</div><p>The Weekly is written every Friday night.</p></div>}

      {lead && (
        <section style={{ borderBottom: '1px solid hsl(var(--studio-border))', paddingBottom: 26, marginBottom: 30 }}>
          <div style={{ ...Mono, fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'hsl(var(--studio-accent))', marginBottom: 10 }}>The Lead · 01{lead.sector_primary ? ` · ${lead.sector_primary}` : ''}</div>
          <div className={leadTickers.length ? 'v3web-lead' : undefined}>
            <div>
              <h2 onClick={() => lead.id && setReaderId(lead.id)} style={{ ...Serif, fontSize: 40, lineHeight: 1.08, letterSpacing: '-0.01em', color: 'hsl(var(--studio-text))', margin: 0, maxWidth: '18ch', textWrap: 'pretty', cursor: lead.id ? 'pointer' : 'default' }}>{lead.headline}</h2>
              {lead.snippet && <p style={{ fontSize: 15, lineHeight: 1.62, color: 'hsl(var(--studio-text-muted))', margin: '16px 0 0', maxWidth: '64ch' }}>{lead.snippet}</p>}
              {lead.id && <button onClick={() => setReaderId(lead.id)} style={{ all: 'unset', cursor: 'pointer', ...Mono, fontSize: 10, letterSpacing: '0.1em', color: 'hsl(var(--studio-accent))', marginTop: 14, display: 'block' }}>READ THE STORY →</button>}
              {/* No rail (no tickers) → keep chips inline as the ticker treatment. */}
              {leadTickers.length === 0 && <TickerChips story={lead} />}
            </div>
            {leadTickers.length > 0 && (
              <div>
                <div className="v3web-leadrail-k">The names</div>
                {leadTickers.map((t) => {
                  const p = leadPrices[t]
                  const c = p?.change1d
                  const up = (c ?? 0) >= 0
                  return (
                    <div key={t} className="v3web-row" onClick={() => openTicker(t)}>
                      <StockLogo ticker={t} size={26} />
                      <div style={{ flex: 1, minWidth: 0 }}><div className="nm">{t}</div></div>
                      <Spark data={barsByTicker[t]} up={up} width={56} height={22} />
                      {p?.price != null && <div className="v3web-num" style={{ fontSize: 12, color: 'hsl(var(--studio-text-muted))', width: 70, textAlign: 'right' }}>${Number(p.price).toFixed(2)}</div>}
                      <div className="v3web-num" style={{ fontSize: 13, color: c == null ? 'hsl(var(--studio-text-subtle))' : `hsl(var(${intent(up)}))`, width: 70, textAlign: 'right' }}>{pct(c)}</div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {BUCKETS.map(([key, label], bi) => {
        const stories = (buckets[key] || []).filter((s) => s !== lead)
        if (stories.length === 0) return null
        return (
          <section key={key} style={{ marginBottom: 34 }}>
            <div className="v3web-kicker" style={{ marginBottom: 16 }}>{label} · 0{bi + 2}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 26 }}>
              {stories.map((s, i) => (
                <div key={s.id || i} onClick={() => s.id && setReaderId(s.id)} style={{ borderTop: '1px solid hsl(var(--studio-border) / 0.5)', paddingTop: 16, cursor: s.id ? 'pointer' : 'default' }}>
                  <h3 style={{ ...Serif, fontSize: 21, lineHeight: 1.24, color: 'hsl(var(--studio-text))', margin: 0, textWrap: 'pretty' }}>{s.headline}</h3>
                  {s.snippet && <p style={{ fontSize: 13, lineHeight: 1.55, color: 'hsl(var(--studio-text-muted))', margin: '8px 0 0' }}>{s.snippet}</p>}
                  <TickerChips story={s} />
                </div>
              ))}
            </div>
          </section>
        )
      })}

      {readerId && <InsightReaderOverlay id={readerId} onClose={() => setReaderId(null)} openTicker={openTicker} />}
    </>
  )
}

function statColor(cls) {
  if (cls === 'up') return '--studio-success'
  if (cls === 'dn' || cls === 'down') return '--studio-danger'
  return '--studio-text'
}

/* Full-article reader — desktop overlay counterpart of the mobile
   /insights/:id InsightReader screen. Same data (useInsightDetail →
   body_md + extracted_stats + tickers + source). */
function InsightReaderOverlay({ id, onClose, openTicker }) {
  const { insight, loading, error } = useInsightDetail(id)

  useEffect(() => {
    const h = (e) => {
      if (e.key !== 'Escape' || document.querySelector('.v3web-cmdk')) return
      e.stopImmediatePropagation?.()
      onClose()
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])

  const stats = insight?.extracted_stats || []
  const tickers = insight?.tickers || []
  const paragraphs = (insight?.body_md || '').split(/\n{2,}/).map((p) => p.trim()).filter(Boolean)

  return (
    <div data-v3web-sheet="1" onMouseDown={onClose} style={{ position: 'fixed', inset: 0, zIndex: 190, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '7vh 20px', background: 'hsl(var(--studio-overlay) / 0.6)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}>
      <div onMouseDown={(e) => e.stopPropagation()} className="atlas-scroll v3web-pop" style={{ width: 'min(680px, 94vw)', maxHeight: '86vh', overflowY: 'auto', background: 'hsl(var(--studio-surface))', border: '1px solid hsl(var(--studio-border))', borderRadius: 'var(--studio-radius-lg)', boxShadow: '0 32px 80px -24px hsl(var(--studio-overlay) / 0.9)', padding: '26px 30px 34px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <span style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.2em', color: 'hsl(var(--studio-accent))' }}>THE ATLAS WEEKLY{insight?.sector_primary ? ` · ${String(insight.sector_primary).toUpperCase()}` : ''}</span>
          <button onClick={onClose} aria-label="Close" style={{ all: 'unset', cursor: 'pointer', padding: 4, color: 'hsl(var(--studio-text-muted))', fontSize: 15, lineHeight: 1 }}>✕</button>
        </div>

        {loading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 13, padding: '6px 0' }}>
            <SkelText w="82%" h={26} />
            <SkelText w="54%" h={26} />
            <SkelText w="94%" h={12} style={{ marginTop: 8 }} />
            <SkelText w="88%" h={12} />
            <SkelText w="91%" h={12} />
            <SkelText w="70%" h={12} />
          </div>
        )}
        {!loading && (error || !insight) && <div style={{ ...Mono, fontSize: 10, letterSpacing: '0.14em', color: 'hsl(var(--studio-danger))', padding: '30px 0' }}>COULDN'T LOAD THIS INSIGHT — TRY REFRESHING.</div>}

        {!loading && insight && (
          <>
            <h1 style={{ ...Serif, fontSize: 31, lineHeight: 1.16, letterSpacing: '-0.01em', color: 'hsl(var(--studio-text))', margin: 0, textWrap: 'pretty' }}>{insight.headline}</h1>
            {insight.snippet && <p style={{ fontSize: 14, lineHeight: 1.6, color: 'hsl(var(--studio-text-muted))', margin: '14px 0 0' }}>{insight.snippet}</p>}

            {stats.length > 0 && (
              <div style={{ marginTop: 22, borderTop: '1px solid hsl(var(--studio-border) / 0.7)' }}>
                {stats.map((s, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 14, padding: '11px 0', borderBottom: i === stats.length - 1 ? 'none' : '1px solid hsl(var(--studio-border) / 0.7)' }}>
                    <span style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'hsl(var(--studio-text-subtle))' }}>{s.label}</span>
                    <span style={{ ...Mono, fontSize: 14, fontVariantNumeric: 'tabular-nums', color: `hsl(var(${statColor(s.color_class)}))` }}>{s.value_text}</span>
                  </div>
                ))}
              </div>
            )}

            {paragraphs.length > 0 ? (
              <div style={{ marginTop: 24 }}>
                {paragraphs.map((p, i) => (
                  <p key={i} style={{ fontSize: 14, lineHeight: 1.72, color: 'hsl(var(--studio-text-muted))', margin: i === 0 ? 0 : '15px 0 0', textWrap: 'pretty' }}>{p}</p>
                ))}
              </div>
            ) : (
              <div style={{ ...Mono, fontSize: 10, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))', marginTop: 24, padding: '12px 14px', border: '1px dashed hsl(var(--studio-border))', borderRadius: 6 }}>
                FULL ANALYSIS GENERATING — REFRESH SHORTLY
              </div>
            )}

            {tickers.length > 0 && (
              <div style={{ marginTop: 26 }}>
                <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.18em', color: 'hsl(var(--studio-text-subtle))', marginBottom: 9 }}>TICKERS</div>
                <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                  {tickers.map((t) => (
                    <button key={t.id || t.ticker} onClick={() => { onClose(); openTicker(t.ticker) }} style={{ ...Mono, fontSize: 11.5, letterSpacing: '0.04em', cursor: 'pointer', color: 'hsl(var(--studio-text))', background: 'hsl(var(--studio-surface-2) / 0.7)', border: '1px solid hsl(var(--studio-border) / 0.6)', borderRadius: 999, padding: '6px 13px' }}>{t.ticker}{t.is_lead ? ' ·' : ''}</button>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 28, paddingTop: 14, borderTop: '1px solid hsl(var(--studio-border) / 0.7)', ...Mono, fontSize: 9.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))' }}>
              <span>{insight.source_name ? `FROM ${insight.source_name}` : 'ATLAS RESEARCH'}{insight.source_date ? ` · ${insight.source_date}` : ''}</span>
              {insight.source_url && <a href={insight.source_url} target="_blank" rel="noopener noreferrer" style={{ color: 'hsl(var(--studio-accent))', textDecoration: 'none' }}>SOURCE ↗</a>}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
